import React from 'react';
import { FileText, Image as ImageIcon, X } from 'lucide-react';

const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Renders the attachments of an announcement. Images go into a thumbnail
 * grid, non-images into stacked link pills. If `onRemove` is provided
 * (HR + editing), each item gets a small delete button.
 */
export default function AttachmentList({ attachments = [], onRemove }) {
  if (!attachments.length) return null;

  const images = attachments.filter(a => a.is_image);
  const files  = attachments.filter(a => !a.is_image);

  return (
    <div className="mt-4 space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {images.map(a => (
            <div key={a.id} className="relative group">
              <a href={a.file} target="_blank" rel="noreferrer" className="block">
                <img
                  src={a.file}
                  alt={a.original_name || 'image'}
                  className="w-full h-32 object-cover rounded-md border border-border"
                  loading="lazy"
                />
              </a>
              {onRemove && (
                <button
                  type="button" onClick={() => onRemove(a)}
                  className="absolute top-1 right-1 h-6 w-6 inline-flex items-center justify-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                  aria-label="Retirer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map(a => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-[14px]">
              <FileText className="h-4 w-4 text-[hsl(var(--text-soft))] shrink-0" />
              <a href={a.file} target="_blank" rel="noreferrer" className="flex-1 truncate text-[hsl(var(--accent))] hover:underline">
                {a.original_name || a.file.split('/').pop()}
              </a>
              {a.size > 0 && <span className="text-[12px] text-[hsl(var(--text-muted))] font-num shrink-0">{fmtSize(a.size)}</span>}
              {onRemove && (
                <button type="button" onClick={() => onRemove(a)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label="Retirer">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Re-export icon so consumers can pick consistent icons.
export { ImageIcon };
